import { Dtos, SuretyAccountId } from "@inrev/common";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useRequest } from "../../../utils/request";
import { accountQueryKey } from "../../account/account.keys";
import { bondRequestPreviewsQueryKey, bondRequestsQueryKey } from "../bond-request.keys";

type SubmitBondRequestVariables = {
	data: Dtos.Admin.SuretyQuote.Create.Request;
	accountId: SuretyAccountId;
	onSuccess: (bondRequest: Dtos.Admin.Quote.Create.Response) => void;
	onError?: (error: Error) => void;
};

export const useAdminSubmitBondRequest = () => {
	const { post } = useRequest();
	const queryClient = useQueryClient();
	const { showSuccessFlag } = useFlags();
	const navigate = useNavigate();

	const mutation = useMutation({
		mutationKey: ["newAdminBondRequestSubmission"],
		mutationFn: async ({ data, accountId }: SubmitBondRequestVariables) => {
			return post<Dtos.Admin.Quote.Create.Response>(
				`/v2/admin/surety/accounts/${accountId}/quotes`,
				data,
			);
		},
		onSuccess: (data, { onSuccess, accountId }) => {
			queryClient.invalidateQueries({ queryKey: bondRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
			showSuccessFlag({
				message: "Bond Request Submitted Successfully",
				actions: [
					{
						label: "View Bond Request",
						onClick: () => navigate(`/accounts/${accountId}/requests/${data.id}`),
						actionId: `${data.id}`,
					},
				],
				autoDismiss: true,
			});
			onSuccess(data);
			return data;
		},
	});

	// Per-call onError, not a mutation-level one: an onError in the useMutation
	// options makes QueryClientProvider skip Sentry logging and the global error
	// modal for every failure of this mutation.
	const { mutate } = mutation;
	const submitBondRequest = useCallback(
		(variables: SubmitBondRequestVariables) => mutate(variables, { onError: variables.onError }),
		[mutate],
	);

	return {
		submitBondRequest,
		isSubmitting: mutation.isPending,
	};
};
